import React, { useState, useEffect } from 'react';
import './Skills.css';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from '../components/SEO';
import skillsImage from '../assets/images/Skills.jpeg';
import { collection, getDocs, getDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import {
  FaBalanceScale, FaFileContract, FaShieldAlt, FaHandshake, FaSearch,
  FaChartLine, FaUserTie, FaLightbulb, FaBullhorn, FaCheckCircle,
  FaTrophy, FaMedal, FaStar, FaAward, FaCertificate, FaGraduationCap,
  FaRocket, FaCrown, FaGem, FaBolt, FaMagic, FaFire, FaGavel,
  FaGlobe, FaDatabase, FaLayerGroup, FaCog, FaBookOpen, FaHeart,
} from 'react-icons/fa';

/* ── ICON REGISTRY ────────────────────────────────────────────── */
const ICON_MAP = {
  FaBalanceScale, FaFileContract, FaShieldAlt, FaHandshake, FaSearch,
  FaChartLine, FaUserTie, FaLightbulb, FaBullhorn, FaCheckCircle,
  FaTrophy, FaMedal, FaStar, FaAward, FaCertificate, FaGraduationCap,
  FaRocket, FaCrown, FaGem, FaBolt, FaMagic, FaFire, FaGavel,
  FaGlobe, FaDatabase, FaLayerGroup, FaCog, FaBookOpen, FaHeart,
};

function Icon({ name }) {
  const C = ICON_MAP[name] || FaFileContract;
  return <C />;
}

/* ── FALLBACK CONFIG (used until Firestore loads) ─────────────── */
const DEFAULT_CONFIG = {
  heroTitle:               "Skills & Achievements",
  heroSubtitle:            "A comprehensive showcase of legal expertise, professional accomplishments, and proven track record in delivering exceptional results",
  heroBadgeText:           "Professional Excellence",
  heroBadgeIcon:           "FaGem",
  heroStats:               [{ value:"800+", label:"Contracts" },{ value:"$750K+", label:"Savings" },{ value:"98%+", label:"Accuracy" }],
  sectionCompetenciesTitle:"Core Competencies",
  sectionAchievementsTitle:"Academic Excellence",
  closingTitle:            "Commitment to Excellence",
  closingText:             "These skills and achievements represent years of dedication, continuous learning, and unwavering commitment to delivering exceptional legal services.",
  commandCockpitLabel:     "Capability cockpit",
  proofLabel:              "Evidence log",
  accentColor1:            "#C25B2A",
  accentColor2:            "#D4AF37",
};

function Skills() {
  const [skills,        setSkills]        = useState([]);
  const [achievements,  setAchievements]  = useState([]);
  const [config,        setConfig]        = useState(DEFAULT_CONFIG);
  const [activeSkillId, setActiveSkillId] = useState(null);
  const [isVisible,     setIsVisible]     = useState(false);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [sSnap, aSnap, cSnap] = await Promise.all([
          getDocs(query(collection(db, "skills_data"),       orderBy("order","asc"))),
          getDocs(query(collection(db, "achievements_data"), orderBy("order","asc"))),
          getDoc(doc(db, "page_config", "skills")),
        ]);
        const skillsList = sSnap.docs.map(d => ({ id:d.id, ...d.data() }));
        setSkills(skillsList);
        setAchievements(aSnap.docs.map(d => ({ id:d.id, ...d.data() })));
        if (cSnap.exists()) setConfig(prev => ({ ...prev, ...cSnap.data() }));
        if (skillsList.length) setActiveSkillId(skillsList[0].id);
      } catch(e) { console.error(e); }
      finally {
        setLoading(false);
        setTimeout(() => setIsVisible(true), 50);
      }
    })();
  }, []);

  const selected = skills.find(s => s.id === activeSkillId) || skills[0];

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="skills-section">
          <div className="skills-container" style={{ display:'flex', justifyContent:'center', paddingTop:'80px' }}>
            <p style={{ color:'#C25B2A', fontFamily:'Playfair Display,serif', fontSize:'1.2rem' }}>Loading…</p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title="Skills & Expertise — Commercial Contracts & Legal Services"
        description="Pooja Biswal's skills in contract law, commercial agreements, negotiation and legal analysis. LLM (Distinction) Queen Mary University of London."
        keywords="contract law skills, commercial contracts expertise, legal negotiation skills, NDA specialist, contract drafting expertise, LLM Queen Mary London"
        canonical="/skills"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Skills", path: "/skills" }]}
      />
      <Navbar />

      <section className="skills-section"
        style={{ '--accent1':config.accentColor1, '--accent2':config.accentColor2 }}>
        <div className={`skills-container ${isVisible ? 'visible' : ''}`}>

          {/* ── HERO ── */}
          <div className="skills-hero">
            <div className="hero-content">
              <div className="hero-text">
                <div className="hero-badge">
                  <Icon name={config.heroBadgeIcon} />
                  <span>{config.heroBadgeText}</span>
                </div>
                <h1 className="hero-title">{config.heroTitle}</h1>
                <p className="hero-subtitle">{config.heroSubtitle}</p>
                <div className="hero-stats">
                  {(config.heroStats || []).map((s, i) => (
                    <div key={i} className="hero-stat">
                      <div className="stat-value">{s.value}</div>
                      <div className="stat-label">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hero-image-wrapper">
                <img src={skillsImage} alt="Skills Showcase" className="hero-image" />
              </div>
            </div>
          </div>

          {/* ── CORE COMPETENCIES ── */}
          {skills.length > 0 && (
            <div className="skills-showcase">
              <div className="section-header">
                <h2 className="section-title">
                  <FaBalanceScale className="section-icon" />
                  <span>{config.sectionCompetenciesTitle}</span>
                </h2>
              </div>

              <div className="skills-command-board" style={{ '--active-color': selected?.color || config.accentColor1 }}>
                <div className="command-copy">
                  <span className="command-kicker">{config.commandCockpitLabel}</span>
                  <h2>{selected?.title}</h2>
                  <p>
                    A focused view of the skill in action: measurable impact, delivery evidence,
                    and the practical strengths behind the headline.
                  </p>
                  <div className="command-metric-row">
                    <div>
                      <strong>{selected?.statsCount}</strong>
                      <span>{selected?.statsLabel}</span>
                    </div>
                    <div>
                      <strong>{selected?.level}%</strong>
                      <span>Readiness</span>
                    </div>
                  </div>
                </div>

                <div className="command-dial" aria-label="Choose a highlighted skill">
                  {skills.map((skill, index) => (
                    <button
                      key={skill.id}
                      type="button"
                      className={activeSkillId === skill.id ? 'active' : ''}
                      style={{
                        '--skill-color': skill.color,
                        '--angle': `${index * (360 / skills.length)}deg`,
                      }}
                      onClick={() => setActiveSkillId(skill.id)}
                      aria-label={`Highlight ${skill.title}`}
                      aria-pressed={activeSkillId === skill.id}
                    >
                      <Icon name={skill.iconKey} />
                    </button>
                  ))}
                  <div className="dial-core">
                    <Icon name={selected?.iconKey} />
                    <span>{selected?.level}%</span>
                  </div>
                </div>

                <div className="command-proof">
                  <span className="proof-label">{config.proofLabel}</span>
                  <ul>
                    {(selected?.achievements || []).map((a, i) => (
                      <li key={i}>
                        <FaCheckCircle />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="skills-grid-modern">
                {skills.map((skill, index) => (
                  <div
                    key={skill.id}
                    className={`skill-card-modern ${activeSkillId === skill.id ? 'active' : ''}`}
                    onClick={() => setActiveSkillId(activeSkillId === skill.id ? null : skill.id)}
                    style={{ '--skill-color': skill.color, '--delay': `${index * 70}ms` }}
                  >
                    <div className="card-header-modern">
                      <div className="icon-wrapper-modern">
                        <div className="skill-icon-modern"><Icon name={skill.iconKey} /></div>
                      </div>
                      <div className="skill-stats">
                        <div className="stat-number">{skill.statsCount}</div>
                        <div className="stat-text">{skill.statsLabel}</div>
                      </div>
                    </div>

                    <h3 className="skill-title-modern">{skill.title}</h3>

                    <div className="skill-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width:`${skill.level}%`, background:`linear-gradient(90deg,${skill.color},#D4AF37)` }}
                        />
                      </div>
                      <div className="progress-label">
                        <span>{skill.level}%</span>
                        <FaFire className="fire-icon" />
                      </div>
                    </div>

                    <div className={`skill-details ${activeSkillId === skill.id ? 'visible' : ''}`}>
                      <ul className="achievements-list">
                        {(skill.achievements || []).map((a, i) => (
                          <li key={i}>
                            <FaCheckCircle className="check-icon" />
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button className="expand-btn">
                      {activeSkillId === skill.id ? 'Show Less' : 'Show Details'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ACADEMIC ACHIEVEMENTS ── */}
          {achievements.length > 0 && (
            <div className="achievements-section">
              <div className="section-header">
                <h2 className="section-title">
                  <FaTrophy className="section-icon" />
                  <span>{config.sectionAchievementsTitle}</span>
                </h2>
              </div>

              <div className="achievements-grid">
                {achievements.map((ach, index) => (
                  <div
                    key={ach.id}
                    className="achievement-card"
                    style={{ '--card-color': ach.color, '--delay': `${index * 80}ms` }}
                  >
                    <div className="achievement-icon" style={{ background: ach.color }}>
                      <Icon name={ach.iconKey} />
                    </div>
                    <div className="achievement-year">{ach.year}</div>
                    <h3 className="achievement-title">{ach.title}</h3>
                    <p className="achievement-subtitle" style={{ color: ach.color }}>{ach.subtitle}</p>
                    <p className="achievement-description">{ach.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── CLOSING ── */}
          <div className="closing-section">
            <div className="closing-icon"><FaCrown /></div>
            <h2 className="closing-title">{config.closingTitle}</h2>
            <p className="closing-text">{config.closingText}</p>
          </div>

        </div>
      </section>

      <Footer />
    </>
  );
}

export default Skills;
